import React from 'react'
import { useTranslation } from 'react-i18next'
import './BranchProgress.scss'

export interface BranchProgressProps {
  /** Erledigte direkte Kinder. */
  done: number
  /** Alle direkten Kinder. Null zeigt nichts. */
  total: number
  className?: string
}

const SIZE = 14
const STROKE = 2
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
/** Schon bei 0 ein Stück Bogen oben — der Ring sagt „geht los", nicht „leer" (Sven, 29.09.2026). */
const MIN_SHARE = 0.05

/**
 * **Unteraufgaben-Fortschritt** einer Eltern-Zeile in der Checkliste (#211): ein runder 1-px-Rahmen, darin ein
 * Ring, der sich von oben im Uhrzeigersinn füllt, und die Zahl „1/3". Sie zählt nur die direkten
 * Kinder (Grill). Steht direkt hinter dem Titel der Zeile.
 *
 * @example
 * <BranchProgress done={0} total={2} />
 */
export const BranchProgress: React.FC<BranchProgressProps> = ({ done, total, className }) => {
  const { t } = useTranslation()
  if (total <= 0) return null
  const share = Math.max(MIN_SHARE, Math.min(1, done / total))
  return (
    <span
      className={['branch-progress', done >= total ? 'branch-progress--complete' : '', className].filter(Boolean).join(' ')}
      role="progressbar"
      aria-valuenow={done}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-label={t('taskItem.subtaskTree.progress', { done, total })}
    >
      <svg className="branch-progress__ring" width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden>
        <circle className="branch-progress__track" cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill="none" strokeWidth={STROKE} />
        <circle
          className="branch-progress__arc"
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${share * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
        />
      </svg>
      <span className="branch-progress__count">{done}/{total}</span>
    </span>
  )
}
